import { useCallback, useEffect, useState } from "react";
import type { User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

// The reader's Data plan (step 2, 29 Oct 2026), read from their own data_subscriptions rows (the table
// lets a reader see only their own). The worker decides what the pages show; this is for the account card
// and the pricing section. Separate from useSubscription: the Club and the Data plans never mix.
export interface DataPlanRow {
  id: string;
  plan: "individual" | "professional";
  period: "monthly" | "annual";
  currency: "INR" | "USD";
  amount: number;
  provider: "cashfree" | "stripe";
  billing: "mandate" | "bank" | "once" | "stripe";
  status: string;
  current_period_end: string | null;
  provisional_until: string | null;
  cancel_at_period_end: boolean;
  created_at: string;
}

// a paid plan, an open ₹1 window, or an approved Cashfree mandate still waiting for its first charge
// (shown so the reader is never offered a second plan; the pages open only once it is paid)
const live = (r: DataPlanRow, now = Date.now()) =>
  (r.status === "active" || r.status === "cancelled") &&
  ((r.current_period_end && new Date(r.current_period_end).getTime() > now) ||
    (r.status === "active" && !r.current_period_end &&
      ((r.provisional_until && new Date(r.provisional_until).getTime() > now) ||
        (r.provider === "cashfree" && r.billing !== "once" &&
          now - new Date(r.created_at).getTime() < (r.billing === "bank" ? 10 : 4) * 86400_000))));

export function useDataPlan(user: User | null) {
  const [current, setCurrent] = useState<DataPlanRow | null>(null);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    if (!user) { setCurrent(null); setLoading(false); return; }
    const { data } = await (supabase as any).from("data_subscriptions")
      .select("id, plan, period, currency, amount, provider, billing, status, current_period_end, provisional_until, cancel_at_period_end, created_at")
      .eq("user_id", user.id).order("created_at", { ascending: false });
    const rows = (data || []) as DataPlanRow[];
    setCurrent(rows.find((r) => live(r)) ?? null);
    setLoading(false);
  }, [user]);
  useEffect(() => { load(); }, [load]);
  return { current, loading, reload: load };
}
